import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { string } from 'prop-types';
import { Card } from '@cards/Card';
import { selectAppUser, selectAppUserCallToActions } from '@redux/appUsers/reducers';
import { fetchAppUsers, fetchAppUserCallToActions } from '@redux/appUsers/actions';
import { Empty } from 'antd';
import CallToActionForm from '@cards/CallToAction/Form';
import ContentRef from '@utils/contentRef';
import CallToActionFormWithItemDB from '@cards/CallToAction/FormWithItemDB';
import './style.less';

function CallToActionDetails({ appUserId, ctaId }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, appUser] = useSelector(selectAppUser(appUserId));
  const [callToActionsLoading, callToActions] = useSelector(selectAppUserCallToActions(appUserId));

  useEffect(() => {
    dispatch(fetchAppUsers());
  }, [dispatch, appUserId]);

  useEffect(() => {
    if (appUser && !callToActions && !callToActionsLoading) {
      dispatch(fetchAppUserCallToActions(appUserId));
    }
  }, [dispatch, appUserId, appUser, callToActions, callToActionsLoading]);

  const isLoading = loading || callToActionsLoading;

  if (appUser === undefined) {
    return (
      <Card
        className="call-to-action-details-card"
        title={t('cards:CallToActionDetails.title')}
        loading={isLoading}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  if (ctaId !== undefined) {
    // Display individual call-to-action
    const ctaIndex = parseInt(ctaId, 10);
    const cta = callToActions && callToActions[ctaIndex];

    if (!cta) {
      return (
        <Card
          className="call-to-action-details-card"
          title={t('cards:CallToActionDetails.title')}
          loading={isLoading}
        >
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Call to action not found" />
        </Card>
      );
    }

    // Check if this is a form-type CTA
    let isFormType = false;
    if (cta.type === 'content_ref' && cta.value) {
      try {
        const parsedRef = ContentRef.fromString(cta.value);
        isFormType = parsedRef.contentType === 'form';
      } catch (error) {
        // Invalid contentRef, fall through to default display
        console.error('Error parsing contentRef:', error);
      }
    }

    let isWoundAssess = false;
    if (cta.data?.experience === 'wound-assess') {
      isWoundAssess = true;
    }
    if (isWoundAssess) {
      return (
        <CallToActionFormWithItemDB
          appUserId={appUserId}
          contentRef={cta.value}
          title={cta.name}
          ctaData={cta.data}
        />
      );
    }

    // Render form component for form-type CTAs
    if (isFormType) {
      return <CallToActionForm appUserId={appUserId} contentRef={cta.value} title={cta.name} />;
    }

    // Default display for other CTA types
    return (
      <Card className="call-to-action-details-card" title={cta.name} loading={isLoading}>
        <div>
          <p>
            <strong>Type:</strong> {cta.type}
          </p>
          {cta.audience && (
            <p>
              <strong>Audience:</strong> {cta.audience}
            </p>
          )}
          <p>
            <strong>Value:</strong> {cta.value}
          </p>
          {cta.icon_name && (
            <p>
              <strong>Icon Name:</strong> {cta.icon_name}
            </p>
          )}
          {cta.icon_url && (
            <div>
              <strong>Icon:</strong>
              <img
                src={cta.icon_url}
                alt={cta.name}
                style={{ maxWidth: '100px', marginLeft: '10px' }}
              />
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Display list of call-to-actions
  return (
    <Card
      className="call-to-action-details-card"
      title={t('cards:CallToActionDetails.title')}
      loading={isLoading}
    >
      {callToActions && callToActions.length > 0 ? (
        <div>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {callToActions.map((cta, index) => (
              <li
                key={index}
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '4px',
                }}
              >
                <div>
                  <strong>{cta.name}</strong>
                  {cta.audience && (
                    <span style={{ marginLeft: '8px', color: '#666' }}>({cta.audience})</span>
                  )}
                </div>
                <div style={{ marginTop: '8px', color: '#666' }}>
                  <span>Type: {cta.type}</span>
                  {cta.value && <span style={{ marginLeft: '16px' }}>Value: {cta.value}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No call-to-actions available" />
      )}
    </Card>
  );
}

CallToActionDetails.propTypes = {
  appUserId: string.isRequired,
  ctaId: string,
};

export default CallToActionDetails;
